import { useForm, router } from "@inertiajs/react"
import { useTranslation } from "react-i18next"
import { useMemo, useEffect, useState } from "react"
import { AlertCircle, AlertTriangle, ArrowRightLeft } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { SearchableSelect } from "@/components/SearchableSelect"
import { TagMultiSelect } from "@/components/TagMultiSelect"
import type { Asset, StageWithRules, SelectOption, ProductOption, MoveStageFormData } from "@/types/inventory"
import type { AssetType } from "@/lib/assetTypes"
import { isoDateInDays } from "@/lib/dates"

// Fields a bulk move can carry over from the selected assets. When they all
// agree the value is prefilled; when they disagree the field is left blank and
// omitted from the request so each asset keeps its own value.
const BULK_FIELDS = ["product_id", "customer_id", "batch_number", "expiration_date", "tag_ids"] as const
type BulkField = (typeof BULK_FIELDS)[number]

function assetFieldValue(asset: Asset, field: BulkField): string {
  switch (field) {
    case "product_id": return asset.product?.id || ""
    case "customer_id": return asset.customer?.id || ""
    case "batch_number": return asset.batch_number || ""
    case "expiration_date": return asset.expiration_date?.split("T")[0] || ""
    case "tag_ids": return asset.tags.map((t) => t.id).sort().join(",")
  }
}

function sharedAssetValues(assets: Asset[]) {
  const shared = {} as Record<BulkField, string>
  const mixed = new Set<BulkField>()
  for (const field of BULK_FIELDS) {
    const first = assets.length > 0 ? assetFieldValue(assets[0], field) : ""
    if (assets.every((a) => assetFieldValue(a, field) === first)) {
      shared[field] = first
    } else {
      shared[field] = ""
      mixed.add(field)
    }
  }
  return { shared, mixed }
}

interface MoveStageDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
  assets: Asset[]
  assetType: AssetType
  selectedIds?: Set<string>
  allCodes?: string[]
  stages: StageWithRules[]
  products: ProductOption[]
  customers: SelectOption[]
  tags: SelectOption[]
}

export function MoveStageDialog({
  open, onOpenChange, onSuccess, assets, assetType, selectedIds, allCodes, stages, products, customers, tags,
}: MoveStageDialogProps) {
  const { t } = useTranslation()
  const bulkCount = selectedIds?.size ?? assets.length
  const isBulk = selectedIds ? bulkCount > 0 : assets.length > 1
  const singleAsset = !isBulk && assets.length === 1 ? assets[0] : null
  const isKeg = assetType === "Keg"

  const form = useForm<MoveStageFormData>({ stage_id: "", product_id: "", customer_id: "", batch_number: "", expiration_date: "", tag_ids: [], notes: "" })
  const [mixedFields, setMixedFields] = useState<Set<BulkField>>(new Set())
  const [touchedFields, setTouchedFields] = useState<Set<BulkField>>(new Set())

  useEffect(() => {
    if (open && singleAsset) {
      form.clearErrors()
      setMixedFields(new Set())
      setTouchedFields(new Set())
      form.setData({ stage_id: singleAsset.stage.id, product_id: singleAsset.product?.id || "", customer_id: singleAsset.customer?.id || "", batch_number: singleAsset.batch_number || "", expiration_date: singleAsset.expiration_date?.split("T")[0] || "", tag_ids: singleAsset.tags.map((t: { id: string }) => t.id), notes: "" })
    } else if (open && isBulk) {
      form.clearErrors()
      const { shared, mixed } = sharedAssetValues(assets)
      setMixedFields(mixed)
      setTouchedFields(new Set())
      form.setData({ stage_id: "", product_id: shared.product_id, customer_id: shared.customer_id, batch_number: shared.batch_number, expiration_date: shared.expiration_date, tag_ids: shared.tag_ids ? shared.tag_ids.split(",") : [], notes: "" })
    }
  }, [open, assets])

  // A mixed field the user hasn't touched is left to the server, which keeps
  // each asset's current value.
  function isMixed(field: BulkField) { return mixedFields.has(field) && !touchedFields.has(field) }
  function setField(field: BulkField, value: string | string[]) {
    setTouchedFields((prev) => new Set(prev).add(field))
    form.setData((data) => ({ ...data, [field]: value }))
  }
  function mixedHint(field: BulkField) {
    if (!isMixed(field)) return null
    return (
      <div className="flex items-start gap-2 rounded-md border border-yellow-200 bg-yellow-50 p-2 text-xs text-yellow-800 dark:border-yellow-900 dark:bg-yellow-950 dark:text-yellow-200">
        <AlertTriangle className="h-4 w-4 shrink-0 text-yellow-600 dark:text-yellow-500" />
        <span>{t("inventory.mixedValuesKept")}</span>
      </div>
    )
  }

  const availableStages = useMemo(() => stages.filter((s) => s.canMoveTo), [stages])
  const selectedStage = useMemo(() => stages.find((s) => s.id === form.data.stage_id), [stages, form.data.stage_id])

  const rules = selectedStage?.rules || []
  const requiresProduct = isKeg && rules.includes("require_product")
  const requiresCustomer = rules.includes("require_customer")
  const requiresBatch = isKeg && rules.includes("require_batch")
  const clearsProduct = isKeg && rules.includes("clear_product")
  const clearsCustomer = rules.includes("clear_customer")
  const clearsTags = rules.includes("clear_tags")
  const clearsLocation = rules.includes("clear_location")
  const clearsBatch = isKeg && rules.includes("clear_batch")

  function handleProductChange(value: string) {
    const selected = products.find((p) => p.id === value)
    const expiration = value && selected?.default_expiration_days ? isoDateInDays(selected.default_expiration_days) : null
    setTouchedFields((prev) => {
      const next = new Set(prev)
      next.add("product_id")
      if (expiration) next.add("expiration_date")
      return next
    })
    form.setData((data) => ({ ...data, product_id: value, ...(expiration ? { expiration_date: expiration } : {}) }))
  }

  const hasErrors = Object.keys(form.errors).length > 0
  const errorMessages = Object.values(form.errors).filter(Boolean) as string[]

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    form.clearErrors()
    // Mixed-and-untouched fields are omitted so the server keeps each asset's
    // own value; every other field is sent, blank included, which clears it.
    const submitData: Record<string, unknown> = { stage_id: form.data.stage_id, notes: form.data.notes }
    if (clearsProduct) {
      submitData.product_id = ""
      submitData.expiration_date = ""
    } else {
      if (!isMixed("product_id")) submitData.product_id = form.data.product_id
      if (!isMixed("expiration_date")) submitData.expiration_date = form.data.expiration_date
    }
    if (clearsBatch) submitData.batch_number = ""
    else if (!isMixed("batch_number")) submitData.batch_number = form.data.batch_number
    if (clearsCustomer) submitData.customer_id = ""
    else if (!isMixed("customer_id")) submitData.customer_id = form.data.customer_id
    if (clearsTags) submitData.tag_ids = []
    else if (!isMixed("tag_ids")) submitData.tag_ids = form.data.tag_ids
    const handleErrors = (errors: Record<string, string | string[]>) => { const fe: Record<string, string> = {}; for (const [k, v] of Object.entries(errors)) { fe[k] = Array.isArray(v) ? v[0] : v }; form.setError(fe) }

    if (isBulk) {
      const assetIds = selectedIds ? Array.from(selectedIds) : assets.map((a) => a.id)
      router.post(`/inventory/bulk_stage_move?asset_type=${assetType}`, { ...submitData, asset_ids: assetIds }, { preserveState: true, onSuccess: () => { form.reset(); onSuccess?.() }, onError: handleErrors })
    } else if (singleAsset) {
      router.post(`/inventory/${singleAsset.id}/stage_move?asset_type=${assetType}`, submitData, { preserveState: true, onSuccess: () => { form.reset(); onOpenChange(false) }, onError: handleErrors })
    }
  }

  function handleOpenChange(newOpen: boolean) { if (!newOpen) { form.reset(); form.clearErrors() }; onOpenChange(newOpen) }

  const assetCodes = allCodes ? allCodes.join(", ") : assets.map((a) => a.code).join(", ")
  const truncatedCodes = assetCodes.length > 50 ? assetCodes.substring(0, 50) + "..." : assetCodes

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ArrowRightLeft className="h-5 w-5" />{t("inventory.moveStage")}</DialogTitle>
            <DialogDescription>{isBulk ? t("inventory.movingAssets", { count: bulkCount }) : t("inventory.movingAsset", { code: singleAsset?.code })}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="text-sm text-muted-foreground" title={assetCodes}>{truncatedCodes}</div>
            {hasErrors && (
              <div className="rounded-md border border-destructive bg-destructive/10 p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-destructive mt-0.5" />
                  <div className="text-sm"><ul className="space-y-1 text-destructive">{errorMessages.map((msg, i) => <li key={i}>{msg}</li>)}</ul></div>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="move-stage">{t("inventory.targetStage")} *</Label>
              <SearchableSelect value={form.data.stage_id} onChange={(value) => form.setData("stage_id", value)} options={availableStages} placeholder={t("inventory.selectStage")} allowEmpty={false} error={!!form.errors.stage_id} />
            </div>
            {selectedStage && rules.length > 0 && (
              <div className="rounded-md border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-900 dark:bg-yellow-950">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-500 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-yellow-800 dark:text-yellow-200">{t("inventory.rulesApplied")}</p>
                    <ul className="mt-1 space-y-1 text-yellow-700 dark:text-yellow-300">
                      {clearsProduct && <li>{t("inventory.ruleWillClearProduct")}</li>}
                      {clearsCustomer && <li>{t("inventory.ruleWillClearCustomer")}</li>}
                      {clearsBatch && <li>{t("inventory.ruleWillClearBatch")}</li>}
                      {clearsTags && <li>{t("inventory.ruleWillClearTags")}</li>}
                      {clearsLocation && <li>{t("inventory.ruleWillClearLocation")}</li>}
                      {requiresProduct && <li>{t("inventory.ruleRequiresProduct")}</li>}
                      {requiresCustomer && <li>{t("inventory.ruleRequiresCustomer")}</li>}
                      {requiresBatch && <li>{t("inventory.ruleRequiresBatch")}</li>}
                    </ul>
                  </div>
                </div>
              </div>
            )}
            {isKeg && !clearsProduct && (
              <div className="space-y-2">
                <Label>{t("inventory.form.productLabel")}{requiresProduct && " *"}</Label>
                <SearchableSelect value={form.data.product_id} onChange={handleProductChange} options={products} placeholder={isMixed("product_id") ? t("inventory.mixedValues") : t("inventory.filters.all")} emptyLabel="-" error={!!form.errors.product_id} />
                {mixedHint("product_id")}
              </div>
            )}
            {isKeg && !clearsProduct && (
              <div className="space-y-2">
                <Label htmlFor="move-expiration">{t("inventory.form.expirationDateLabel")}</Label>
                <Input id="move-expiration" type="date" value={form.data.expiration_date} onChange={(e) => setField("expiration_date", e.target.value)} className={form.errors.expiration_date ? "border-destructive" : ""} />
                {mixedHint("expiration_date")}
              </div>
            )}
            {isKeg && requiresBatch && (
              <div className="space-y-2">
                <Label>{t("inventory.form.batchNumberLabel")} *</Label>
                <Input value={form.data.batch_number} onChange={(e) => setField("batch_number", e.target.value)} placeholder={isMixed("batch_number") ? t("inventory.mixedValues") : t("inventory.form.batchNumberPlaceholder")} className={form.errors.batch_number ? "border-destructive" : ""} />
                {mixedHint("batch_number")}
              </div>
            )}
            {!clearsCustomer && (
              <div className="space-y-2">
                <Label>{t("inventory.form.customerLabel")}{requiresCustomer && " *"}</Label>
                <SearchableSelect value={form.data.customer_id} onChange={(value) => setField("customer_id", value)} options={customers} placeholder={isMixed("customer_id") ? t("inventory.mixedValues") : t("inventory.filters.all")} emptyLabel="-" error={!!form.errors.customer_id} />
                {mixedHint("customer_id")}
              </div>
            )}
            {!clearsTags && (
              <div className="space-y-2">
                <Label>{t("inventory.form.tagsLabel")}</Label>
                <TagMultiSelect tags={tags} selectedIds={form.data.tag_ids} onChange={(ids) => setField("tag_ids", ids)} />
                {mixedHint("tag_ids")}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="move-notes">{t("inventory.form.notesLabel")}</Label>
              <Textarea id="move-notes" value={form.data.notes} onChange={(e) => form.setData("notes", e.target.value)} placeholder={t("inventory.form.notesPlaceholder")} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>{t("common.cancel")}</Button>
            <Button type="submit" disabled={form.processing || !form.data.stage_id}><ArrowRightLeft className="h-4 w-4 mr-2" />{t("inventory.moveButton")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
